// @ts-nocheck
import React, { memo, useState, Fragment, useEffect } from 'react'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { Checkbox } from 'components/forms'
import { FormModal } from 'transfix-ui'
import { useCarrierStateQuery } from '../../Cards/CarrierStatus/graphql/CarrierState'
import { useTransfixRulesAssessmentQuery } from '../../Cards/CarrierStatus/graphql/TransfixRulesAssessment'
import { BCA } from '../BCA'
import { SafetyLetter } from '../SafetyLetter'
import { EditPackageFilesRow } from './EditPackageFilesRow'
import { ReviewedDocument } from './ReviewedDocument'

const SAFETY_LETTER_REVIEWED = 'safetyLetterReviewedById'
const SAFETY_LETTER_NO_APPLICABLE = 'safetyLetterNotApplicableReviewedById'
const SAFETY_LETTER_OPTIONS = [
  {
    label: 'Reviewed',
    value: SAFETY_LETTER_REVIEWED,
    id: SAFETY_LETTER_REVIEWED,
  },
  {
    label: 'Not Applicable',
    value: SAFETY_LETTER_NO_APPLICABLE,
    id: SAFETY_LETTER_NO_APPLICABLE,
  },
]
const reviewedBy = (checklist, category) =>
  checklist && checklist[category] ? checklist[category].fullName : undefined

interface IEditChecklistDialogProps {
  carrierId?: number
  checklists?: {
    brokerCarrierAgreement?: {
      createdDate?: string
    }
    hideBrokerCarrierAgreement: any
  }
  dialogOpen?: boolean
  closeDialog: (...args: any[]) => any
  saveChecklist: (...args: any[]) => any
  addDocUpload?: (...args: any[]) => any
  deleteDocUpload?: (...args: any[]) => any
  getUploadedDocs: (...args: any[]) => any
  documents?: {
    fileUrl?: string
    fileFileName?: string
    file?: string
    pending?: any
    _destroy?: any
  }[]
  dialogDocuments?: {
    fileUrl?: string
    fileFileName?: string
    file?: string
  }[]
  uploadDocument: (...args: any[]) => any
  error?: string
  checklistError?: string
}
export const EditChecklistDialog: React.FunctionComponent<IEditChecklistDialogProps> = memo(
  ({
    addDocUpload,
    carrierId,
    checklistError,
    checklists,
    closeDialog,
    deleteDocUpload,
    dialogDocuments,
    dialogOpen = false,
    documents,
    error,
    getUploadedDocs,
    saveChecklist,
    uploadDocument,
  }) => {
    const { userDetails, userPermissions } = useUserDetails()
    const { id: userId } = userDetails || {}
    const editChecklists = userPermissions?.canManageCarrierChecklist

    const rows = checklists
      ? [
          {
            key: 'w9FormReviewedById',
            label: 'W9 Form',
            value: reviewedBy(checklists, 'w9FormReviewedBy'),
          },
          {
            key: 'operatingAuthorityReviewedById',
            label: 'Operating Authority (both Federal and State if applicable)',
            value: reviewedBy(checklists, 'operatingAuthorityReviewedBy'),
          },
          {
            key: 'fmcsaInfoVerifiedById',
            label: 'FMCSA',
            value: reviewedBy(checklists, 'fmcsaInfoVerifiedBy'),
          },
          {
            key: 'fsmaInfoReviewedById',
            label: 'FSMA',
            value: reviewedBy(checklists, 'fsmaInfoReviewedBy'),
          },
        ]
      : []

    const [selected, setSelected] = useState({})

    const handleChange = item => {
      const {
        target: { id },
      } = item
      setSelected(current => ({
        ...current,
        [id]: current[id] ? null : userId,
      }))
    }

    const handleSafetyLetter = ({ target: { value } }) => {
      setSelected(current => ({
        ...current,
        [SAFETY_LETTER_REVIEWED]: value === SAFETY_LETTER_REVIEWED ? userId : undefined,
        [SAFETY_LETTER_NO_APPLICABLE]: value === SAFETY_LETTER_NO_APPLICABLE ? userId : undefined,
      }))
    }

    const { refetch: stateRefetch } =
      useCarrierStateQuery({ variables: { carrierApplicationId: String(carrierId) } }) || {}

    const { refetch: transfixRulesRefetch } = useTransfixRulesAssessmentQuery({
      variables: { carrierApplicationId: String(carrierId) },
    })

    const safetyLetterEditMode =
      !reviewedBy(checklists, 'safetyLetterReviewedBy') &&
      !reviewedBy(checklists, 'safetyLetterNotApplicableReviewedBy')

    const safetyLetterReviewedBySelected =
      selected[SAFETY_LETTER_REVIEWED] && SAFETY_LETTER_REVIEWED
    const safetyLetterNotApplicableSelected =
      selected[SAFETY_LETTER_NO_APPLICABLE] && SAFETY_LETTER_NO_APPLICABLE
    const safetyLetterValue =
      safetyLetterReviewedBySelected || safetyLetterNotApplicableSelected || undefined

    const onSubmit = async () => {
      const docs = documents ? dialogDocuments : dialogDocuments
      const files = docs
        .filter(document => (document.pending ? !document._destroy : document._destroy))
        .map(document => {
          if (document.pending) return { file: document.file, fileFileName: document.fileFileName }
          return document
        })
      if (Object.keys(selected).length > 0) {
        saveChecklist(carrierId, selected)
      }

      await uploadDocument(carrierId, files)
      stateRefetch()
      transfixRulesRefetch()
    }

    const onClose = () => {
      setSelected({})
      getUploadedDocs(carrierId)
      closeDialog()
    }

    useEffect(() => {
      setSelected(current => ({
        ...current,
        hideBrokerCarrierAgreement: checklists?.hideBrokerCarrierAgreement,
      }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <FormModal
        title='Edit Documents'
        size={6}
        open={dialogOpen}
        onClose={onClose}
        dataTest='edit-checklist'
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            dataTest: 'cancel-checklist',
          },
          {
            label: 'Save',
            type: 'confirm',
            action: onSubmit,
            dataTest: 'save-checklist',
          },
        ]}
        error={[error, checklistError].filter(Boolean).join(' ')}>
        {checklists && (
          <div className='pv4'>
            <BCA
              brokerCarrierAgreement={checklists.brokerCarrierAgreement}
              hideBrokerCarrierAgreement={checklists.hideBrokerCarrierAgreement}
            />
            {!checklists.brokerCarrierAgreement && (
              <Checkbox
                label='Signed BCA on file'
                name='bca_on_file'
                key='hideBrokerCarrierAgreement'
                id='hideBrokerCarrierAgreement'
                checked={!!selected.hideBrokerCarrierAgreement}
                onChange={handleChange}
                dataTest='bca-on-file'
              />
            )}
          </div>
        )}
        {rows.map(item => (
          <Fragment key={item.key}>
            {item.value ? (
              <ReviewedDocument type={item.key} checklists={checklists} />
            ) : (
              <div className='pv4'>
                {item.label}
                <Checkbox
                  key={item.key}
                  id={item.key}
                  label='Reviewed'
                  onChange={handleChange}
                  checked={!!selected[item.key]}
                  dataTest={item.key}
                  disabled={!editChecklists}
                />
              </div>
            )}
          </Fragment>
        ))}

        <div className='pv4'>
          {safetyLetterEditMode ? (
            <RadioGroup
              label='Safety Letter (if applicable)'
              stacked
              required={false}
              name='safetyLetter'
              onOptionChange={handleSafetyLetter}
              disabled={!editChecklists}
              checked={safetyLetterValue}
              options={SAFETY_LETTER_OPTIONS}
            />
          ) : (
            <SafetyLetter
              safetyLetter={checklists.safetyLetterReviewedBy}
              notApplicableSafetyLetter={checklists.safetyLetterNotApplicableReviewedBy}
            />
          )}
        </div>

        <EditPackageFilesRow
          dialogDocuments={dialogDocuments}
          addDocUpload={addDocUpload}
          deleteDocUpload={deleteDocUpload}
        />
      </FormModal>
    )
  }
)
